
import { supabase } from '../../supabase';
import { Paper, PaperAuthor } from '@/types/paper';
import { mapToPaper, deduplicateAuthors } from '../paper-mappers';

import { adminLog, adminError } from '../paper-stats';

/**
 * Get a paper by ID
 * @param id Paper ID to fetch
 * @returns Paper object or null if not found
 */
export async function getPaperById(id: string): Promise<Paper | null> {
  try {
    if (!id || typeof id !== 'string') {
      adminError('Invalid paper ID:', id);
      return null;
    }
    
    adminLog('Fetching paper with ID:', id);

    // Single RPC: paper + versions + platform author profiles in one round-trip.
    // Falls back to multi-query path on RPC failure for resilience.
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_paper_full', { p_id: id });

    let paper: any = null;
    let versions: any[] = [];
    let authorUsers: Array<{ id: string; name: string | null; institution: string | null; role: string | null }> = [];

    if (rpcError) {
      adminError('get_paper_full RPC failed, falling back:', rpcError);
    } else if (rpcData) {
      paper = (rpcData as any).paper ?? null;
      versions = (rpcData as any).versions ?? [];
      authorUsers = (rpcData as any).author_users ?? [];
    }

    if (!paper && !rpcError) {
      adminLog('Paper not found with ID:', id);
      return null;
    }

    // Fallback path if RPC errored
    if (!paper) {
      const [paperResponse, versionsResponse] = await Promise.all([
        supabase.from('papers').select('*').eq('id', id).maybeSingle(),
        supabase
          .from('paper_versions')
          .select('id, paper_id, version, title, abstract, file_url, external_url, url, published_at, changes, created_at, research_question, approach, primary_finding, implication, supplementary_materials')
          .eq('paper_id', id),
      ]);
      if (paperResponse.error) {
        adminError('Error fetching paper:', paperResponse.error);
        return null;
      }
      if (!paperResponse.data) return null;
      if (versionsResponse.error) {
        adminError('Error fetching paper versions:', versionsResponse.error);
        return null;
      }
      paper = paperResponse.data;
      versions = versionsResponse.data || [];
    }

    const mappedPaper = mapToPaper(paper, versions);

    if (mappedPaper.authors && mappedPaper.authors.length > 0 && authorUsers.length > 0) {
      const userMap = new Map(authorUsers.map(u => [u.id, u]));
      mappedPaper.authors = mappedPaper.authors.map(author => {
        if (author.isPlatformUser && author.id && userMap.has(author.id)) {
          const userData = userMap.get(author.id)!;
          return {
            ...author,
            name: userData.name || author.name,
            institution: userData.institution || author.institution,
            role: userData.role || author.role,
          };
        }
        return author;
      });
    }
    
    // IMPORTANT: Do not add the primary author to the beginning of the list
    // as that would disrupt the correct author ordering
    
    adminLog('Successfully fetched and mapped paper:', mappedPaper);
    return mappedPaper;
  } catch (error) {
    adminError('Unexpected error in getPaperById:', error);
    return null;
  }
}
